'use client';

import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export function AdminMigrations() {
  const [isRunningEnum, setIsRunningEnum] = useState(false);
  const [resultEnum, setResultEnum] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [isRunningGenre, setIsRunningGenre] = useState(false);
  const [resultGenre, setResultGenre] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [isRunningConcentric, setIsRunningConcentric] = useState(false);
  const [resultConcentric, setResultConcentric] = useState<{ success: boolean; message?: string; error?: string } | null>(null);
  const [isRunningArchPeriods, setIsRunningArchPeriods] = useState(false);
  const [resultArchPeriods, setResultArchPeriods] = useState<{ success: boolean; message?: string; error?: string } | null>(null);

  const runEnumMigration = async () => {
    if (!confirm('Are you sure you want to run the migration to fix invalid enum values? This will update data in Firestore.')) {
      return;
    }

    setIsRunningEnum(true);
    setResultEnum(null);

    try {
      const functions = getFunctions();
      const runFixInvalidEnumValuesMigration = httpsCallable(functions, 'runFixInvalidEnumValuesMigration');

      const response = await runFixInvalidEnumValuesMigration();
      const data = response.data as { success: boolean; message?: string; error?: string };

      setResultEnum(data);
    } catch (error: any) {
      setResultEnum({
        success: false,
        error: error.message || 'Unknown error occurred',
      });
    } finally {
      setIsRunningEnum(false);
    }
  };

  const runGenreMigration = async () => {
    if (!confirm('Are you sure you want to run the migration to fix invalid genre values? This will update data in Firestore.')) {
      return;
    }

    setIsRunningGenre(true);
    setResultGenre(null);

    try {
      const functions = getFunctions();
      const runFixInvalidGenreValuesMigration = httpsCallable(functions, 'runFixInvalidGenreValuesMigration');

      const response = await runFixInvalidGenreValuesMigration();
      const data = response.data as { success: boolean; message?: string; error?: string };

      setResultGenre(data);
    } catch (error: any) {
      setResultGenre({
        success: false,
        error: error.message || 'Unknown error occurred',
      });
    } finally {
      setIsRunningGenre(false);
    }
  };

  const runConcentricPatternMigration = async () => {
    if (!confirm('Are you sure you want to run the migration to fix "Concentric Pattern" -> "Chiasm"? This will update data in Firestore.')) {
      return;
    }

    setIsRunningConcentric(true);
    setResultConcentric(null);

    try {
      const functions = getFunctions();
      const runFixConcentricPatternMigration = httpsCallable(functions, 'runFixConcentricPatternMigration');

      const response = await runFixConcentricPatternMigration();
      const data = response.data as { success: boolean; message?: string; error?: string };

      setResultConcentric(data);
    } catch (error: any) {
      setResultConcentric({
        success: false,
        error: error.message || 'Unknown error occurred',
      });
    } finally {
      setIsRunningConcentric(false);
    }
  };

  const runArchaeologyPeriodsMigration = async () => {
    if (!confirm('Are you sure you want to run the migration to convert archaeology periods from string to array? This will update data in Firestore.')) {
      return;
    }

    setIsRunningArchPeriods(true);
    setResultArchPeriods(null);

    try {
      const functions = getFunctions();
      const runMigrateArchaeologyPeriodsToArray = httpsCallable(functions, 'runMigrateArchaeologyPeriodsToArray');

      const response = await runMigrateArchaeologyPeriodsToArray();
      const data = response.data as { success: boolean; message?: string; error?: string };

      setResultArchPeriods(data);
    } catch (error: any) {
      setResultArchPeriods({
        success: false,
        error: error.message || 'Unknown error occurred',
      });
    } finally {
      setIsRunningArchPeriods(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Data Migrations</CardTitle>
          <CardDescription>
            Run one-time migrations to fix data issues
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Fix Invalid Enum Values</h3>
            <p className="text-sm text-muted-foreground">
              This migration fixes invalid enum values in the database:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Period values: "Iron Age IIA/IIB/IIC" → "Iron Age II", "Bronze Age" → "Middle Bronze Age", "Old Kingdom" → "Early Bronze Age", "New Kingdom" → "Late Bronze Age", "Ottoman Period" → "Byzantine Period", etc.</li>
              <li>Chronology values: "Historical" → "Archaeological", "Standard History" → "Consensus"</li>
              <li>Category values: "culture" → "cultural", "social" → "cultural", "biblical-background" → "biblical-narrative", "archaeological" → "cultural"</li>
              <li>Confidence values: "debated" → "disputed", "majority" → "high"</li>
              <li>Calendar dates: "late summer" → "summer", remove incomplete dates with null fields</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Affected collections: archaeologyByPassage, archaeologyByBook, cultureByPassage, cultureByBook, placesByPassage, places, historicalEventsByPassage, historicalEventsByBook
            </p>
            
            <Button
              onClick={runEnumMigration}
              disabled={isRunningEnum}
              className="mt-4"
            >
              {isRunningEnum ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running Migration...
                </>
              ) : (
                'Run Migration'
              )}
            </Button>
          </div>

          {resultEnum && (
            <Alert variant={resultEnum.success ? 'default' : 'destructive'}>
              {resultEnum.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>{resultEnum.success ? 'Success' : 'Error'}</AlertTitle>
              <AlertDescription>
                {resultEnum.success ? resultEnum.message : resultEnum.error}
                {resultEnum.success && (
                  <p className="mt-2 text-sm">
                    Check the Firebase Functions logs for detailed information about what was changed.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2 mt-8">
            <h3 className="text-lg font-semibold">Fix Invalid Genre Values</h3>
            <p className="text-sm text-muted-foreground">
              This migration fixes invalid genre/subgenre/form combinations in passages:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Maps subgenre-only values to full genre/subgenre/form paths (e.g., Wisdom → Instruction → Wisdom → Proverbial Wisdom → Instruction)</li>
              <li>Fixes typos (e.g., "Paraenesise" → "Paranesis")</li>
              <li>Normalizes narrative and poetry forms to match the genre taxonomy</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Affected collection: passages
            </p>

            <Button
              onClick={runGenreMigration}
              disabled={isRunningGenre}
              className="mt-4"
            >
              {isRunningGenre ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running Migration...
                </>
              ) : (
                'Run Genre Migration'
              )}
            </Button>
          </div>

          {resultGenre && (
            <Alert variant={resultGenre.success ? 'default' : 'destructive'}>
              {resultGenre.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>{resultGenre.success ? 'Success' : 'Error'}</AlertTitle>
              <AlertDescription>
                {resultGenre.success ? resultGenre.message : resultGenre.error}
                {resultGenre.success && (
                  <p className="mt-2 text-sm">
                    Check the Firebase Functions logs for detailed information about what was changed.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2 mt-8">
            <h3 className="text-lg font-semibold">Fix Concentric Pattern → Chiasm</h3>
            <p className="text-sm text-muted-foreground">
              This migration standardizes literary structure terminology:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Changes "Concentric Pattern" to "Chiasm" (they are the same thing)</li>
              <li>Ensures consistent terminology across all literary features</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Affected collections: literaryFeaturesByPassage, literaryFeaturesByBook
            </p>

            <Button
              onClick={runConcentricPatternMigration}
              disabled={isRunningConcentric}
              className="mt-4"
            >
              {isRunningConcentric ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running Migration...
                </>
              ) : (
                'Run Concentric Pattern Migration'
              )}
            </Button>
          </div>

          {resultConcentric && (
            <Alert variant={resultConcentric.success ? 'default' : 'destructive'}>
              {resultConcentric.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>{resultConcentric.success ? 'Success' : 'Error'}</AlertTitle>
              <AlertDescription>
                {resultConcentric.success ? resultConcentric.message : resultConcentric.error}
                {resultConcentric.success && (
                  <p className="mt-2 text-sm">
                    Check the Firebase Functions logs for detailed information about what was changed.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2 mt-8">
            <h3 className="text-lg font-semibold">Migrate Archaeology Periods to Array</h3>
            <p className="text-sm text-muted-foreground">
              This migration converts the archaeology period field from a single string to an array of strings:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>Single periods: "Iron Age II" → ["Iron Age II"]</li>
              <li>Multi-period findings: "Late Bronze Age / Iron Age I" → ["Late Bronze Age", "Iron Age I"]</li>
              <li>Handles various delimiters: " / ", ", ", " - ", "-"</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Affected collections: archaeologyByPassage, archaeologyByBook
            </p>

            <Button
              onClick={runArchaeologyPeriodsMigration}
              disabled={isRunningArchPeriods}
              className="mt-4"
            >
              {isRunningArchPeriods ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running Migration...
                </>
              ) : (
                'Run Archaeology Periods Migration'
              )}
            </Button>
          </div>

          {resultArchPeriods && (
            <Alert variant={resultArchPeriods.success ? 'default' : 'destructive'}>
              {resultArchPeriods.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>{resultArchPeriods.success ? 'Success' : 'Error'}</AlertTitle>
              <AlertDescription>
                {resultArchPeriods.success ? resultArchPeriods.message : resultArchPeriods.error}
                {resultArchPeriods.success && (
                  <p className="mt-2 text-sm">
                    Check the Firebase Functions logs for detailed information about what was changed.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

